/**
 * NotificheLogTab — storico notifiche push/email inviate ai lavoratori
 * per un cantiere/manutenzione. Admin only.
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/api";

const CANALE_STYLES = {
  push:  { label: "📱 Push",  cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  email: { label: "✉️ Email", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  none:  { label: "— Nessuno", cls: "bg-slate-100 text-slate-600 border-slate-300" },
};

const ESITO_STYLES = {
  delivered: { label: "✅ Recapitata", cls: "text-emerald-700" },
  failed:    { label: "❌ Fallita",    cls: "text-rose-700" },
};

const TIPO_LABEL = {
  attivita_assegnata: "🛠️ Attività assegnata",
};

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function NotificheLogTab({ cantiereId }) {
  const [filtroCanale, setFiltroCanale] = React.useState("");
  const [filtroEsito, setFiltroEsito] = React.useState("");
  const [filtroLav, setFiltroLav] = React.useState("");

  const { data: items = [], isLoading, refetch } = useQuery({
    queryKey: ["notifiche-log", cantiereId],
    queryFn: () => api.get(`/cantieri/${cantiereId}/notifiche-log`).then((r) => r.data),
    enabled: !!cantiereId,
  });

  const filtered = items.filter((n) => {
    if (filtroCanale && n.canale !== filtroCanale) return false;
    if (filtroEsito && n.esito !== filtroEsito) return false;
    if (filtroLav && !(n.lavoratore_nome || "").toLowerCase().includes(filtroLav.toLowerCase())) return false;
    return true;
  });

  const stats = React.useMemo(() => {
    const s = { totali: items.length, push: 0, email: 0, delivered: 0, failed: 0 };
    for (const n of items) {
      if (n.canale === "push") s.push += 1;
      if (n.canale === "email") s.email += 1;
      if (n.esito === "delivered") s.delivered += 1;
      if (n.esito === "failed") s.failed += 1;
    }
    return s;
  }, [items]);

  return (
    <div className="bg-white border border-slate-200" data-testid="notifiche-log-tab">
      <div className="p-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="overline">Storico notifiche</div>
          <h3 className="font-display font-bold">
            {stats.totali} notifich{stats.totali === 1 ? "a" : "e"} inviata
            {stats.totali === 1 ? "" : "e"}
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Push e email spedite ai lavoratori quando vengono assegnati ad attività di questo cantiere.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          data-testid="btn-refresh-notifiche-log"
          className="px-3 py-1.5 text-xs font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-sm"
        >
          ↻ Aggiorna
        </button>
      </div>

      {/* KPI riga */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-b border-slate-200 text-center">
        <Kpi label="Totali" value={stats.totali} />
        <Kpi label="Push" value={stats.push} tone="emerald" />
        <Kpi label="Email" value={stats.email} tone="blue" />
        <Kpi label="Recapitate" value={stats.delivered} tone="emerald" />
        <Kpi label="Fallite" value={stats.failed} tone="rose" />
      </div>

      {/* Filtri */}
      <div className="p-3 flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50 text-xs">
        <span className="font-bold uppercase tracking-widest text-slate-500 text-[10px]">Filtri:</span>
        <select
          value={filtroCanale}
          onChange={(e) => setFiltroCanale(e.target.value)}
          data-testid="filter-canale"
          className="px-2 py-1 border border-slate-300 rounded-sm bg-white"
        >
          <option value="">Tutti i canali</option>
          <option value="push">📱 Solo push</option>
          <option value="email">✉️ Solo email</option>
          <option value="none">— Non consegnate</option>
        </select>
        <select
          value={filtroEsito}
          onChange={(e) => setFiltroEsito(e.target.value)}
          data-testid="filter-esito"
          className="px-2 py-1 border border-slate-300 rounded-sm bg-white"
        >
          <option value="">Tutti gli esiti</option>
          <option value="delivered">✅ Recapitate</option>
          <option value="failed">❌ Fallite</option>
        </select>
        <input
          type="text"
          placeholder="🔎 Cerca lavoratore…"
          value={filtroLav}
          onChange={(e) => setFiltroLav(e.target.value)}
          data-testid="filter-lav"
          data-no-uppercase
          className="px-2 py-1 border border-slate-300 rounded-sm w-40"
        />
      </div>

      {/* Tabella */}
      {isLoading ? (
        <div className="p-8 text-center text-slate-400 text-sm">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-center text-slate-500 text-sm">
          {items.length === 0 ? (
            <>Nessuna notifica ancora inviata.<br />
              <span className="text-[11px] text-slate-400">
                Quando assegni un lavoratore ad un'attività del Cronoprogramma, comparirà qui.
              </span>
            </>
          ) : (
            "Nessuna notifica corrisponde ai filtri."
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left p-2 text-[11px] uppercase tracking-widest">Quando</th>
                <th className="text-left p-2 text-[11px] uppercase tracking-widest">Tipo</th>
                <th className="text-left p-2 text-[11px] uppercase tracking-widest">Destinatario</th>
                <th className="text-left p-2 text-[11px] uppercase tracking-widest">Canale</th>
                <th className="text-left p-2 text-[11px] uppercase tracking-widest">Esito</th>
                <th className="text-left p-2 text-[11px] uppercase tracking-widest">Contenuto</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((n) => {
                const c = CANALE_STYLES[n.canale] || CANALE_STYLES.none;
                const e = ESITO_STYLES[n.esito] || { label: n.esito, cls: "text-slate-600" };
                return (
                  <tr
                    key={n.log_id}
                    className="border-t border-slate-100 hover:bg-slate-50 align-top"
                    data-testid={`row-notif-${n.log_id}`}
                  >
                    <td className="p-2 font-mono text-[11px] whitespace-nowrap">{fmtDT(n.created_at)}</td>
                    <td className="p-2 text-xs whitespace-nowrap">
                      {TIPO_LABEL[n.tipo] || n.tipo}
                      {n.urgente && (
                        <div className="mt-0.5 inline-block px-1.5 py-0.5 bg-rose-600 text-white rounded-sm text-[9px] font-bold uppercase">
                          ⚠️ urgente
                        </div>
                      )}
                    </td>
                    <td className="p-2 text-xs">
                      <div className="font-semibold">{n.lavoratore_nome || n.lavoratore_id}</div>
                      {n.destinatario_email && (
                        <div className="text-[10px] text-slate-500 font-mono">{n.destinatario_email}</div>
                      )}
                    </td>
                    <td className="p-2">
                      <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-semibold border ${c.cls}`}>
                        {c.label}
                      </span>
                    </td>
                    <td className={`p-2 text-xs font-semibold ${e.cls}`}>
                      {e.label}
                      {n.errore && (
                        <div className="text-[10px] text-slate-500 font-normal mt-0.5 italic max-w-xs">
                          {n.errore}
                        </div>
                      )}
                    </td>
                    <td className="p-2 text-xs text-slate-700 max-w-md">
                      <div className="font-semibold">{n.titolo}</div>
                      <div className="text-[11px] text-slate-500 whitespace-pre-wrap">{n.messaggio}</div>
                      {n.attivita_nome && (
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          Attività: {n.attivita_nome}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, tone = "slate" }) {
  const tones = {
    slate:   "text-slate-900",
    emerald: "text-emerald-700",
    blue:    "text-blue-700",
    rose:    "text-rose-700",
  };
  return (
    <div className="p-3 border-r last:border-r-0 border-slate-200">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-display font-black text-2xl ${tones[tone] || tones.slate}`}>{value}</div>
    </div>
  );
}
